import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function IconX() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

// ── PM lookup component ────────────────────────────────────────────────────────

function PmLookup({ onAdd }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen]           = useState(false);
  const [error, setError]         = useState('');
  const debounceRef               = useRef(null);
  const wrapperRef                = useRef(null);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    setSearching(true);
    setError('');
    try {
      const res = await axios.get('/employees', { params: { q } });
      setResults(res.data.data || []);
      setOpen(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Employee lookup failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  function handleInput(e) {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  }

  function handleSelect(emp) {
    onAdd({ pm_name: emp.name, pm_email: emp.email, pm_phone: emp.phone });
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', zIndex: open ? 50 : 'auto' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-muted)', pointerEvents: 'none',
          }}>
            <IconSearch />
          </span>
          <input
            className="form-input"
            style={{ paddingLeft: 32 }}
            placeholder="Search by name or email…"
            value={query}
            onChange={handleInput}
            onFocus={() => results.length > 0 && setOpen(true)}
          />
        </div>
        {searching && <div className="spinner" style={{ width: 16, height: 16, flexShrink: 0 }} />}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{error}</div>
      )}

      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          maxHeight: 240, overflowY: 'auto', marginTop: 4,
        }}>
          {results.map((emp, i) => (
            <button
              key={emp.id || i}
              type="button"
              onClick={() => handleSelect(emp)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 14px', background: 'none', border: 'none',
                cursor: 'pointer', borderBottom: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.name}</div>
              {(emp.email || emp.phone) && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {emp.email}{emp.email && emp.phone ? ' · ' : ''}{emp.phone}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {open && results.length === 0 && !searching && query.trim() && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '12px 14px',
          color: 'var(--text-muted)', fontSize: 13, marginTop: 4,
        }}>
          No employees found for "{query}"
        </div>
      )}
    </div>
  );
}

// ── PM row ─────────────────────────────────────────────────────────────────────

function PmRow({ pm, idx, onChange, onRemove }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto',
      gap: 10, alignItems: 'start', padding: '12px 0',
      borderBottom: '1px solid var(--border)',
    }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label" style={{ fontSize: 11 }}>
          Name <span style={{ color: 'var(--danger)' }}>*</span>
        </label>
        <input
          className="form-input"
          placeholder="Full name"
          value={pm.pm_name}
          onChange={e => onChange(idx, 'pm_name', e.target.value)}
        />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Email</label>
        <input
          className="form-input"
          placeholder="email@example.com"
          value={pm.pm_email}
          onChange={e => onChange(idx, 'pm_email', e.target.value)}
        />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Phone</label>
        <input
          className="form-input"
          placeholder="+91-…"
          value={pm.pm_phone}
          onChange={e => onChange(idx, 'pm_phone', e.target.value)}
        />
      </div>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ marginTop: 22, color: 'var(--danger)', padding: '6px 8px' }}
        onClick={() => onRemove(idx)}
        title="Remove PM"
      >
        <IconX />
      </button>
    </div>
  );
}

// ── Main form ──────────────────────────────────────────────────────────────────

const EMPTY_PM = { pm_name: '', pm_email: '', pm_phone: '' };

export default function ProductForm() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);

  const [name, setName]       = useState('');
  const [pms, setPms]         = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`/products/${id}`)
      .then(res => {
        setName(res.data.name || '');
        setPms((res.data.pms || []).map(pm => ({
          pm_name:  pm.pm_name  || '',
          pm_email: pm.pm_email || '',
          pm_phone: pm.pm_phone || '',
        })));
      })
      .catch(() => setError('Failed to load product'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  function handleAddPmFromLookup(emp) {
    setPms(prev => [...prev, { pm_name: emp.pm_name, pm_email: emp.pm_email, pm_phone: emp.pm_phone }]);
  }

  function handleAddBlankPm() {
    setPms(prev => [...prev, { ...EMPTY_PM }]);
  }

  function handleChangePm(idx, field, value) {
    setPms(prev => prev.map((pm, i) => i === idx ? { ...pm, [field]: value } : pm));
  }

  function handleRemovePm(idx) {
    setPms(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setNameError('Product name is required'); return; }
    setNameError('');

    const payload = {
      name: name.trim(),
      pms: pms
        .filter(pm => pm.pm_name.trim())
        .map(pm => ({
          pm_name:  pm.pm_name.trim(),
          pm_email: pm.pm_email.trim() || null,
          pm_phone: pm.pm_phone.trim() || null,
        })),
    };

    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await axios.put(`/products/${id}`, payload);
        navigate(`/products/${id}`);
      } else {
        const res = await axios.post('/products', payload);
        navigate(`/products/${res.data.id}`);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading product…</span>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>{isEdit ? 'Edit Product' : 'New Product'}</h2>
        <p>{isEdit ? `Editing ${name}` : 'Create a product and assign product managers'}</p>
      </div>

      <div className="page-body" style={{ maxWidth: 860 }}>
        <button
          className="btn btn-ghost"
          style={{ marginBottom: 20, paddingLeft: 0 }}
          onClick={() => navigate(isEdit ? `/products/${id}` : '/products')}
        >
          <IconArrowLeft /> Back
        </button>

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {/* Product name */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="product_name">
                Product Name <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="product_name"
                className="form-input"
                placeholder="e.g. Voice Campaigns"
                value={name}
                onChange={e => { setName(e.target.value); setNameError(''); }}
                style={{ maxWidth: 400 }}
              />
              {nameError && (
                <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{nameError}</div>
              )}
            </div>
          </div>

          {/* Product Managers */}
          <div className="card">
            <div style={{
              fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
              marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>Product Managers</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                {pms.length} PM{pms.length !== 1 ? 's' : ''} assigned
              </span>
            </div>

            {/* Lookup */}
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Search &amp; add from employee directory</label>
              <PmLookup onAdd={handleAddPmFromLookup} />
            </div>

            {/* PM rows */}
            {pms.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {pms.map((pm, idx) => (
                  <PmRow
                    key={idx}
                    pm={pm}
                    idx={idx}
                    onChange={handleChangePm}
                    onRemove={handleRemovePm}
                  />
                ))}
              </div>
            )}

            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              onClick={handleAddBlankPm}
            >
              <IconPlus /> Add PM manually
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(isEdit ? `/products/${id}` : '/products')}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                : isEdit ? 'Save Changes' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
